import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, BookMarked, Check, Trash2, Loader2, CalendarClock, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

const TotalsRow = ({ totals }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    <Metric label="Copie" value={totals.units} />
    <Metric label="Royalty lorda" value={fmtEuro(totals.gross)} />
    <Metric label="Quota autore" value={fmtEuro(totals.author_share)} gold />
    <Metric label="Già pagate autore" value={fmtEuro(totals.paid_to_author)} />
  </div>
);

const Metric = ({ label, value, gold }) => (
  <div className="rounded-xl border border-white/5 bg-[#0A1124] p-4">
    <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
    <p className={`mt-1 font-serif text-2xl font-light ${gold ? "text-[#D4AF37]" : "text-white"}`}>{value}</p>
  </div>
);

const RoyaltyRow = ({ entry, sharePct, kind, isAdmin, onPaid, onDelete, onEdit }) => {
  const [busy, setBusy] = useState(false);
  const share = entry.author_share_amount ?? Number(((entry.royalty_gross || 0) * sharePct) / 100);
  const paid = Boolean(entry.paid_at);
  const overdue = !paid && entry.expected_author_payout_date && new Date(entry.expected_author_payout_date) < new Date();
  return (
    <tr className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/5 p-4 first:border-t-0 md:table-row md:p-0 md:first:border-t" data-testid={`royalty-row-${entry.royalty_id}`}>
      <td className="order-1 font-serif text-white md:table-cell md:py-3 md:pr-2">{entry.period}</td>
      <td className="order-3 w-full text-xs text-white/70 md:table-cell md:w-auto md:py-3 md:pr-2 md:text-sm">
        <span className="mr-1.5 text-[9px] uppercase tracking-wider text-white/30 md:hidden">{kind === "kindle" ? "ASIN" : "ISBN"}</span>
        {kind === "kindle" ? (entry.asin || "—") : (entry.isbn || "—")}
      </td>
      <td className="order-4 md:table-cell md:py-3 md:pr-2 md:text-right">
        <span className="mr-1.5 text-[9px] uppercase tracking-wider text-white/30 md:hidden">Copie</span>
        <span className="text-white/80">{entry.units_sold}</span>
      </td>
      <td className="order-5 md:table-cell md:py-3 md:pr-2 md:text-right">
        <span className="mr-1.5 text-[9px] uppercase tracking-wider text-white/30 md:hidden">Prezzo</span>
        <span className="text-white/80">{fmtEuro(entry.list_price)}</span>
      </td>
      <td className="order-6 md:table-cell md:py-3 md:pr-2 md:text-right">
        <span className="mr-1.5 text-[9px] uppercase tracking-wider text-white/30 md:hidden">Roy. %</span>
        <span className="text-white/80">{entry.royalty_pct}%</span>
      </td>
      <td className="order-7 md:table-cell md:py-3 md:pr-2 md:text-right">
        <span className="mr-1.5 text-[9px] uppercase tracking-wider text-white/30 md:hidden">Lorda</span>
        <span className="text-white">{fmtEuro(entry.royalty_gross)}</span>
      </td>
      <td className="order-8 md:table-cell md:py-3 md:pr-2 md:text-right">
        <span className="mr-1.5 text-[9px] uppercase tracking-wider text-white/30 md:hidden">Quota autore</span>
        <span className="text-[#D4AF37]">{fmtEuro(share)}</span>
      </td>
      <td className="order-9 text-white/50 md:table-cell md:py-3 md:pr-2">
        <span className="flex items-center gap-1.5 text-xs">
          <CalendarClock className="h-3 w-3" />
          {fmtDate(entry.expected_author_payout_date)}
        </span>
      </td>
      <td className="order-2 ml-auto md:table-cell md:py-3 md:pr-2 md:text-right">
        {paid ? (
          <span className="rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 px-2.5 py-1 text-[11px]">
            Pagata · {fmtDate(entry.paid_at)}
          </span>
        ) : overdue ? (
          <span className="rounded-full bg-orange-400/10 border border-orange-400/30 text-orange-300 px-2.5 py-1 text-[11px]">
            In ritardo
          </span>
        ) : (
          <span className="rounded-full bg-white/5 border border-white/10 text-white/60 px-2.5 py-1 text-[11px]">
            Da pagare
          </span>
        )}
      </td>
      {isAdmin && (
        <td className="order-10 w-full md:table-cell md:w-auto md:py-3 md:text-right">
          <div className="flex justify-end gap-1">
            {!paid && (
              <Button
                size="sm"
                onClick={async () => { setBusy(true); try { await onPaid(entry.royalty_id); } finally { setBusy(false); } }}
                disabled={busy}
                data-testid={`royalty-pay-${entry.royalty_id}`}
                className="h-7 gap-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 px-2.5 text-[11px]"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Pagata
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(entry)}
              data-testid={`royalty-edit-${entry.royalty_id}`}
              className="h-7 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5 px-2"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => { if (confirm("Eliminare questa riga royalty?")) await onDelete(entry.royalty_id); }}
              data-testid={`royalty-delete-${entry.royalty_id}`}
              className="h-7 rounded-full border-white/10 bg-transparent text-white/50 hover:bg-white/5 px-2"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
};

const AddKindleForm = ({ bookId, onSaved }) => {
  const [form, setForm] = useState({ period: "", asin: "", list_price: "", units_sold: "", royalty_pct: 70, delivery_cost: 0.10 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.period || !form.list_price || form.units_sold === "") { toast.error("Compila periodo, prezzo e copie"); return; }
    setBusy(true);
    try {
      await api.post(`/books/${bookId}/royalties/kindle`, {
        period: form.period,
        asin: form.asin || null,
        list_price: Number(form.list_price),
        units_sold: Number(form.units_sold),
        royalty_pct: Number(form.royalty_pct),
        delivery_cost: Number(form.delivery_cost || 0),
      });
      toast.success("Royalty Kindle registrata");
      setForm({ period: "", asin: "", list_price: "", units_sold: "", royalty_pct: 70, delivery_cost: 0.10 });
      await onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/5 bg-[#0A1124] p-5">
      <p className="mb-4 font-serif text-lg text-white">Aggiungi royalty Kindle</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Periodo" placeholder="Gennaio 2026" value={form.period} onChange={set("period")} testid="k-period" />
        <Field label="ASIN" placeholder="B0DXYZ1234" value={form.asin} onChange={set("asin")} testid="k-asin" />
        <Field label="Prezzo di listino (€)" type="number" placeholder="4.99" value={form.list_price} onChange={set("list_price")} testid="k-price" />
        <Field label="Copie vendute" type="number" placeholder="100" value={form.units_sold} onChange={set("units_sold")} testid="k-units" />
        <Field label="Royalty %" type="number" placeholder="70" value={form.royalty_pct} onChange={set("royalty_pct")} testid="k-pct" />
        <Field label="Delivery cost/unità" type="number" step="0.01" placeholder="0.10" value={form.delivery_cost} onChange={set("delivery_cost")} testid="k-delivery" />
      </div>
      <Button onClick={submit} disabled={busy} data-testid="k-save" className="mt-4 gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Registra
      </Button>
    </div>
  );
};

const AddPaperbackForm = ({ bookId, onSaved }) => {
  const [form, setForm] = useState({ period: "", isbn: "", list_price: "", units_sold: "", royalty_pct: 60, print_cost_per_unit: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.period || !form.list_price || form.units_sold === "" || !form.print_cost_per_unit) { toast.error("Compila i campi obbligatori"); return; }
    setBusy(true);
    try {
      await api.post(`/books/${bookId}/royalties/paperback`, {
        period: form.period,
        isbn: form.isbn || null,
        list_price: Number(form.list_price),
        units_sold: Number(form.units_sold),
        royalty_pct: Number(form.royalty_pct),
        print_cost_per_unit: Number(form.print_cost_per_unit),
      });
      toast.success("Royalty cartaceo registrata");
      setForm({ period: "", isbn: "", list_price: "", units_sold: "", royalty_pct: 60, print_cost_per_unit: "" });
      await onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/5 bg-[#0A1124] p-5">
      <p className="mb-4 font-serif text-lg text-white">Aggiungi royalty Cartaceo</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Periodo" placeholder="Gennaio 2026" value={form.period} onChange={set("period")} testid="p-period" />
        <Field label="ISBN" placeholder="9788012345678" value={form.isbn} onChange={set("isbn")} testid="p-isbn" />
        <Field label="Prezzo di listino (€)" type="number" placeholder="14.90" value={form.list_price} onChange={set("list_price")} testid="p-price" />
        <Field label="Copie vendute" type="number" placeholder="30" value={form.units_sold} onChange={set("units_sold")} testid="p-units" />
        <Field label="Royalty %" type="number" placeholder="60" value={form.royalty_pct} onChange={set("royalty_pct")} testid="p-pct" />
        <Field label="Costo stampa/unità (€)" type="number" step="0.01" placeholder="3.80" value={form.print_cost_per_unit} onChange={set("print_cost_per_unit")} testid="p-print" />
      </div>
      <Button onClick={submit} disabled={busy} data-testid="p-save" className="mt-4 gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Registra
      </Button>
    </div>
  );
};

const Field = ({ label, testid, ...rest }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-white/40">{label}</Label>
    <Input {...rest} data-testid={testid}
      className="mt-1 border-white/10 bg-white/5 text-white placeholder:text-white/25" />
  </div>
);

const RoyaltyTable = ({ kind, entries, sharePct, isAdmin, onPaid, onDelete, onEdit }) => {
  if (!entries || entries.length === 0) {
    return <p className="mt-6 text-white/40">Nessuna royalty {kind === "kindle" ? "Kindle" : "cartaceo"} registrata.</p>;
  }
  return (
    <div className="mt-6 rounded-xl border border-white/5 bg-[#0A1124] md:overflow-x-auto">
      <table className="block w-full text-sm md:table md:min-w-[900px]">
        <thead className="hidden md:table-header-group">
          <tr className="text-left text-[11px] uppercase tracking-widest text-white/40">
            <th className="p-3">Periodo</th>
            <th className="p-3">{kind === "kindle" ? "ASIN" : "ISBN"}</th>
            <th className="p-3 text-right">Copie</th>
            <th className="p-3 text-right">Prezzo</th>
            <th className="p-3 text-right">Royalty %</th>
            <th className="p-3 text-right">Lorda</th>
            <th className="p-3 text-right">Quota autore</th>
            <th className="p-3">Pagamento autore</th>
            <th className="p-3 text-right">Stato</th>
            {isAdmin && <th className="p-3 text-right">Azioni</th>}
          </tr>
        </thead>
        <tbody className="block md:table-row-group">
          {entries.map((e) => (
            <RoyaltyRow key={e.royalty_id} entry={e} sharePct={sharePct} kind={kind}
              isAdmin={isAdmin} onPaid={onPaid} onDelete={onDelete} onEdit={onEdit} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EditRoyaltyDialog = ({ bookId, entry, onClose, onSaved }) => {
  const isPaperback = entry?.kind === "paperback";
  const [form, setForm] = useState(entry ? {
    kind: entry.kind || "kindle",
    period: entry.period || "",
    asin: entry.asin || "",
    isbn: entry.isbn || "",
    list_price: entry.list_price ?? "",
    units_sold: entry.units_sold ?? "",
    royalty_pct: entry.royalty_pct ?? (isPaperback ? 60 : 70),
    delivery_cost: entry.delivery_cost ?? 0.10,
    print_cost_per_unit: entry.print_cost_per_unit ?? "",
  } : {});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!entry) return;
    const payload = {
      kind: form.kind,
      period: form.period || null,
      list_price: form.list_price === "" ? null : Number(form.list_price),
      units_sold: form.units_sold === "" ? null : Number(form.units_sold),
      royalty_pct: form.royalty_pct === "" ? null : Number(form.royalty_pct),
    };
    if (form.kind === "kindle") {
      payload.asin = form.asin || null;
      payload.delivery_cost = form.delivery_cost === "" ? null : Number(form.delivery_cost);
    } else {
      payload.isbn = form.isbn || null;
      payload.print_cost_per_unit = form.print_cost_per_unit === "" ? null : Number(form.print_cost_per_unit);
    }
    setBusy(true);
    try {
      await api.put(`/books/${bookId}/royalties/${entry.royalty_id}`, payload);
      toast.success("Royalty aggiornata");
      await onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!entry) return null;
  return (
    <Dialog open={!!entry} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl border-white/10 bg-[#0A1124] text-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">Modifica royalty</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/40">Tipo</Label>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => setForm({ ...form, kind: "kindle" })}
                data-testid="edit-kind-kindle"
                className={`flex-1 rounded-xl border p-3 text-left transition-all ${form.kind === "kindle" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/20"}`}>
                <p className="flex items-center gap-2 text-sm"><BookOpen className="h-4 w-4" /> Kindle (eBook)</p>
              </button>
              <button type="button" onClick={() => setForm({ ...form, kind: "paperback" })}
                data-testid="edit-kind-paperback"
                className={`flex-1 rounded-xl border p-3 text-left transition-all ${form.kind === "paperback" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/20"}`}>
                <p className="flex items-center gap-2 text-sm"><BookMarked className="h-4 w-4" /> Cartaceo</p>
              </button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Periodo" placeholder="Gennaio 2026" value={form.period} onChange={set("period")} testid="edit-period" />
            {form.kind === "kindle" ? (
              <Field label="ASIN" value={form.asin} onChange={set("asin")} testid="edit-asin" />
            ) : (
              <Field label="ISBN" value={form.isbn} onChange={set("isbn")} testid="edit-isbn" />
            )}
            <Field label="Prezzo di listino (€)" type="number" step="0.01" value={form.list_price} onChange={set("list_price")} testid="edit-price" />
            <Field label="Copie vendute" type="number" value={form.units_sold} onChange={set("units_sold")} testid="edit-units" />
            <Field label="Royalty %" type="number" value={form.royalty_pct} onChange={set("royalty_pct")} testid="edit-pct" />
            {form.kind === "kindle" ? (
              <Field label="Delivery cost/unità" type="number" step="0.01" value={form.delivery_cost} onChange={set("delivery_cost")} testid="edit-delivery" />
            ) : (
              <Field label="Costo stampa/unità (€)" type="number" step="0.01" value={form.print_cost_per_unit} onChange={set("print_cost_per_unit")} testid="edit-print" />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-royalty-save"
            className="gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Salva modifiche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ShareEditor = ({ bookId, currentPct, onSaved }) => {
  const [pct, setPct] = useState(currentPct);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/books/${bookId}`, { author_share_pct: Number(pct) });
      toast.success(`Quota autore aggiornata: ${pct}%`);
      await onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
      <span className="text-xs uppercase tracking-widest text-white/40">Quota autore</span>
      <Input value={pct} onChange={(e) => setPct(e.target.value)} type="number"
        className="h-7 w-16 border-white/10 bg-transparent text-center text-white" data-testid="royalty-share-input" />
      <span className="text-sm text-white/60">%</span>
      <Button size="sm" onClick={save} disabled={busy} data-testid="royalty-share-save"
        className="h-7 rounded-full bg-[#D4AF37] px-3 text-[11px] text-[#050A15] hover:bg-[#E5C158]">
        {busy ? "…" : "Salva"}
      </Button>
    </div>
  );
};

export const RoyaltySection = ({ book, royalty, isAdmin, onRefresh }) => {
  const sharePct = royalty.author_share_pct || 50;
  const bookId = book.book_id;
  const [editing, setEditing] = useState(null);

  const payRoyalty = async (roy_id) => {
    try {
      await api.post(`/books/${bookId}/royalties/${roy_id}/pay`, {});
      toast.success("Royalty segnata come pagata. Notifica inviata all'autore.");
      await onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delRoyalty = async (roy_id) => {
    try {
      await api.delete(`/books/${bookId}/royalties/${roy_id}`);
      toast.success("Riga eliminata");
      await onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-light text-white">Gestione royalty</h2>
          <p className="mt-1 text-sm text-white/50">
            Amazon KDP paga ~60 giorni dopo la fine del mese. La quota autore viene bonificata entro <strong className="text-white">15 giorni</strong> dopo l'accredito Amazon.
          </p>
        </div>
        {isAdmin && (
          <ShareEditor bookId={bookId} currentPct={sharePct} onSaved={onRefresh} />
        )}
      </div>

      <Tabs defaultValue="kindle">
        <TabsList className="bg-[#0A1124] border border-white/10">
          <TabsTrigger value="kindle" data-testid="royalty-tab-kindle"
            className="gap-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050A15]">
            <BookOpen className="h-4 w-4" /> Kindle
          </TabsTrigger>
          <TabsTrigger value="paperback" data-testid="royalty-tab-paperback"
            className="gap-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050A15]">
            <BookMarked className="h-4 w-4" /> Cartaceo
          </TabsTrigger>
        </TabsList>

        <TabsContent value="kindle" className="mt-6" data-testid="royalty-panel-kindle">
          <TotalsRow totals={royalty.kindle.totals} />
          {isAdmin && <div className="mt-6"><AddKindleForm bookId={bookId} onSaved={onRefresh} /></div>}
          <RoyaltyTable kind="kindle" entries={royalty.kindle.entries} sharePct={sharePct}
            isAdmin={isAdmin} onPaid={payRoyalty} onDelete={delRoyalty} onEdit={setEditing} />
        </TabsContent>

        <TabsContent value="paperback" className="mt-6" data-testid="royalty-panel-paperback">
          <TotalsRow totals={royalty.paperback.totals} />
          {isAdmin && <div className="mt-6"><AddPaperbackForm bookId={bookId} onSaved={onRefresh} /></div>}
          <RoyaltyTable kind="paperback" entries={royalty.paperback.entries} sharePct={sharePct}
            isAdmin={isAdmin} onPaid={payRoyalty} onDelete={delRoyalty} onEdit={setEditing} />
        </TabsContent>
      </Tabs>

      <EditRoyaltyDialog
        key={editing?.royalty_id || "none"}
        bookId={bookId}
        entry={editing}
        onClose={() => setEditing(null)}
        onSaved={onRefresh}
      />
    </div>
  );
};
